import { useEffect, useRef } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../../../lib/supabase";
import { useThemeMode } from "../../../theme/ThemeModeProvider";
import { chatRailTokens } from "../tokens";
import { MessageBubble, type MessageRow } from "./MessageBubble";
import { ApprovalCard } from "./ApprovalCard";
import { ThinkingIndicator } from "./ThinkingIndicator";
import { useChatRail } from "./ChatRailProvider";
import type { MemoryCitation } from "./MemoryCitationChip";

// Statuses where the agent is actively working on a turn — drives the
// "Thinking…" indicator. paused_for_* are deliberately excluded: those
// surface their own UI (input prompt or approval card), so a thinking
// indicator on top would be misleading.
const ACTIVE_RUN_STATUSES = ["queued", "running"] as const;

interface CitationRow {
  id: string;
  message_id: number;
  memory_id: string;
  ord: number;
  start_char: number;
  end_char: number;
}

interface ApprovalRow {
  id: string;
  run_id: string;
  status: string;
  expires_at: string;
  rationale: string | null;
  proposed_input: unknown;
}

export function ChatThread({
  agentId,
  conversationId,
}: {
  agentId: string;
  conversationId: string;
}) {
  const { mode } = useThemeMode();
  const T = chatRailTokens(mode);
  const { setDrawerRunId } = useChatRail();
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const { data: messages = [], isLoading } = useQuery({
    queryKey: ["conversation-messages", conversationId],
    queryFn: async (): Promise<MessageRow[]> => {
      const { data, error } = await supabase
        .from("agent_messages_view")
        .select("*")
        .eq("conversation_id", conversationId)
        .order("id", { ascending: true });
      if (error) throw error;
      return (data ?? []) as MessageRow[];
    },
    refetchInterval: 5000,
  });

  const runIds = Array.from(new Set(messages.map((m) => m.run_id).filter((x): x is string => !!x)));
  const messageIds = messages.map((m) => m.id);

  const { data: citationRows = [] } = useQuery({
    queryKey: ["conversation-citations", conversationId, messageIds.join(",")],
    enabled: messageIds.length > 0,
    queryFn: async (): Promise<CitationRow[]> => {
      const { data, error } = await supabase
        .from("message_citations_view")
        .select("id, message_id, memory_id, ord, start_char, end_char")
        .in("message_id", messageIds);
      if (error) throw error;
      return (data ?? []).filter(
        (r): r is CitationRow =>
          typeof r.id === "string" &&
          typeof r.message_id === "number" &&
          typeof r.memory_id === "string" &&
          typeof r.ord === "number" &&
          typeof r.start_char === "number" &&
          typeof r.end_char === "number",
      );
    },
    staleTime: 60_000,
  });

  const memoryIds = Array.from(new Set(citationRows.map((c) => c.memory_id)));
  const { data: memoryRows = [] } = useQuery({
    queryKey: ["citation-memories", memoryIds.join(",")],
    enabled: memoryIds.length > 0,
    queryFn: async (): Promise<{ id: string; content: string }[]> => {
      const { data, error } = await supabase
        .from("agent_memory_view")
        .select("id, content")
        .in("id", memoryIds);
      if (error) throw error;
      return (data ?? []).filter(
        (r): r is { id: string; content: string } =>
          typeof r.id === "string" && typeof r.content === "string",
      );
    },
    staleTime: 60_000,
  });

  const memoryById = new Map(memoryRows.map((m) => [m.id, m.content]));
  const citationsByMessage = new Map<number, MemoryCitation[]>();
  for (const c of citationRows) {
    const list = citationsByMessage.get(c.message_id) ?? [];
    list.push({
      id: c.id,
      memory_id: c.memory_id,
      ord: c.ord,
      start_char: c.start_char,
      end_char: c.end_char,
      excerpt: memoryById.get(c.memory_id) ?? "",
    });
    citationsByMessage.set(c.message_id, list);
  }

  const { data: activeRunsCount = 0 } = useQuery({
    queryKey: ["conversation-active-runs", conversationId],
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from("agent_runs_view")
        .select("id", { count: "exact", head: true })
        .eq("conversation_id", conversationId)
        .in("status", ACTIVE_RUN_STATUSES as unknown as string[]);
      if (error) throw error;
      return count ?? 0;
    },
    refetchInterval: 2000,
  });
  const isWorking = activeRunsCount > 0;

  const { data: pendingApprovals = [] } = useQuery({
    queryKey: ["conversation-approvals", conversationId, runIds.join(",")],
    enabled: runIds.length > 0,
    queryFn: async (): Promise<ApprovalRow[]> => {
      const { data, error } = await supabase
        .from("approvals_view")
        .select("id, run_id, status, expires_at, rationale, proposed_input")
        .eq("status", "pending")
        .in("run_id", runIds);
      if (error) throw error;
      return (data ?? []) as ApprovalRow[];
    },
    refetchInterval: 5000,
  });

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, pendingApprovals.length, isWorking]);

  if (isLoading) {
    return (
      <Box flex={1} display="flex" alignItems="center" justifyContent="center">
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (messages.length === 0 && !isWorking) {
    return (
      <Box
        ref={scrollerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: T.textMute,
          fontSize: T.fs_meta,
          fontFamily: T.sans,
          p: 3,
        }}
      >
        Start the conversation below.
      </Box>
    );
  }

  return (
    <Box
      ref={scrollerRef}
      sx={{
        flex: 1,
        overflowY: "auto",
        px: 2,
        py: 2,
        bgcolor: T.bg,
      }}
    >
      {messages.map((m) => (
        <Box key={m.id} sx={{ mb: 1.5 }}>
          <MessageBubble
            message={m}
            agentId={agentId}
            citations={citationsByMessage.get(m.id) ?? []}
            onOpenRun={(runId) => setDrawerRunId(runId)}
          />
        </Box>
      ))}
      {pendingApprovals.map((a) => (
        <Box key={a.id} sx={{ mb: 1.5 }}>
          <ApprovalCard
            approvalId={a.id}
            runId={a.run_id}
            expiresAt={a.expires_at}
            rationale={a.rationale}
            proposedInput={a.proposed_input}
            onOpenRun={(runId) => setDrawerRunId(runId)}
          />
        </Box>
      ))}
      {isWorking && pendingApprovals.length === 0 && (
        <Box sx={{ mb: 1.5 }}>
          <ThinkingIndicator />
        </Box>
      )}
    </Box>
  );
}
